import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import {
  ensureFirebaseAuth,
  firebaseDb,
  isFirebaseConfigured,
  tryEnsureFirebaseAuth,
} from "data/firebase";

const APPLICATIONS = "kadekonApplications";
const APPLICATIONS_LEGACY = "supportRequests";
const APPLICATION_KIND = "kadekonApplication";
const MEMBERS = "kadekonMembers";
const QRS = "kadekonQrCodes";

function requireDb() {
  if (!isFirebaseConfigured || !firebaseDb) {
    throw new Error("Firebase yapılandırılmamış.");
  }
  return firebaseDb;
}

function nowIso() {
  return new Date().toISOString();
}

function makeId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function omitUndefined(data) {
  return Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined && value !== ""));
}

export function formatKadekonPhone(phoneKey) {
  const key = String(phoneKey || "");
  if (key.length === 12 && key.startsWith("90")) {
    return `0${key.slice(2, 5)} ${key.slice(5, 8)} ${key.slice(8, 10)} ${key.slice(10, 12)}`;
  }
  if (!key) return "";
  return key.startsWith("+") ? key : `+${key}`;
}

function asApplication(row) {
  const source = row.source;
  return {
    id: String(row.id || ""),
    fullName: String(row.fullName || ""),
    nationalId: String(row.nationalId || ""),
    birthDate: String(row.birthDate || ""),
    birthPlace: String(row.birthPlace || ""),
    phone: String(row.phone || ""),
    phoneKey: String(row.phoneKey || ""),
    status: row.status === "approved" || row.status === "rejected" ? row.status : "pending",
    source: source === "ios" || source === "android" || source === "web" ? source : undefined,
    userId: row.userId ? String(row.userId) : undefined,
    email: row.email ? String(row.email) : undefined,
    createdAt: String(row.createdAt || ""),
    updatedAt: String(row.updatedAt || ""),
    reviewedAt: row.reviewedAt ? String(row.reviewedAt) : undefined,
    reviewNote: row.reviewNote ? String(row.reviewNote) : undefined,
    collection: row.collection || APPLICATIONS,
  };
}

function asMember(row) {
  return {
    id: String(row.id || ""),
    fullName: String(row.fullName || ""),
    nationalId: String(row.nationalId || ""),
    birthDate: String(row.birthDate || ""),
    birthPlace: String(row.birthPlace || ""),
    phone: String(row.phone || ""),
    phoneKey: String(row.phoneKey || ""),
    status: row.status === "inactive" ? "inactive" : "active",
    applicationId: row.applicationId ? String(row.applicationId) : undefined,
    userId: row.userId ? String(row.userId) : undefined,
    email: row.email ? String(row.email) : undefined,
    createdAt: String(row.createdAt || ""),
    updatedAt: String(row.updatedAt || ""),
  };
}

function asQr(row) {
  return {
    id: String(row.id || ""),
    memberId: String(row.memberId || ""),
    memberName: String(row.memberName || ""),
    phone: String(row.phone || ""),
    phoneKey: String(row.phoneKey || ""),
    token: String(row.token || ""),
    payload: String(row.payload || ""),
    status: row.status === "revoked" || row.status === "expired" ? row.status : "active",
    createdAt: String(row.createdAt || ""),
    updatedAt: String(row.updatedAt || ""),
    revokedAt: row.revokedAt ? String(row.revokedAt) : undefined,
  };
}

function isKadekonApplicationRow(row) {
  if (typeof row.kind === "string" && row.kind !== APPLICATION_KIND) return false;
  if (row.kind === APPLICATION_KIND) return true;
  return String(row.id || "").startsWith("kap_");
}

async function listCollectionSafe(collectionName) {
  const db = requireDb();
  try {
    const snap = await getDocs(collection(db, collectionName));
    return snap.docs.map((item) => ({ id: item.id, ...(item.data() || {}) }));
  } catch {
    return [];
  }
}

async function listWhereSafe(collectionName, field, value) {
  const db = requireDb();
  try {
    const snap = await getDocs(query(collection(db, collectionName), where(field, "==", value)));
    return snap.docs.map((item) => ({ id: item.id, ...(item.data() || {}) }));
  } catch {
    const all = await listCollectionSafe(collectionName);
    return all.filter((row) => String(row[field] || "") === String(value));
  }
}

async function getApplicationRecord(id) {
  const db = requireDb();
  const primary = await getDoc(doc(db, APPLICATIONS, id));
  if (primary.exists()) {
    return { row: { id: primary.id, ...primary.data() }, collection: APPLICATIONS };
  }
  const legacy = await getDoc(doc(db, APPLICATIONS_LEGACY, id));
  if (legacy.exists() && isKadekonApplicationRow({ id: legacy.id, ...legacy.data() })) {
    return { row: { id: legacy.id, ...legacy.data() }, collection: APPLICATIONS_LEGACY };
  }
  return null;
}

export async function listKadekonApplicationsFromFirestore() {
  requireDb();
  await tryEnsureFirebaseAuth();
  const primary = await listCollectionSafe(APPLICATIONS);
  const legacy = (await listCollectionSafe(APPLICATIONS_LEGACY)).filter(isKadekonApplicationRow);
  const primaryIds = new Set(primary.map((row) => String(row.id)));
  const byId = new Map();
  for (const row of [...legacy, ...primary]) {
    byId.set(String(row.id), {
      ...row,
      collection: primaryIds.has(String(row.id)) ? APPLICATIONS : APPLICATIONS_LEGACY,
    });
  }
  return [...byId.values()]
    .map(asApplication)
    .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
}

export async function listKadekonMembersFromFirestore() {
  requireDb();
  await tryEnsureFirebaseAuth();
  const rows = await listCollectionSafe(MEMBERS);
  return rows.map(asMember).sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
}

export async function listKadekonQrCodesFromFirestore() {
  requireDb();
  await tryEnsureFirebaseAuth();
  const rows = await listCollectionSafe(QRS);
  return rows.map(asQr).sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
}

export async function approveKadekonApplicationInFirestore(applicationId) {
  const db = requireDb();
  await ensureFirebaseAuth();
  const found = await getApplicationRecord(applicationId);
  if (!found) throw new Error("Başvuru bulunamadı.");
  const application = asApplication({ ...found.row, collection: found.collection });
  if (application.status !== "pending") {
    throw new Error("Bu başvuru zaten işlenmiş.");
  }

  const existing = (await listWhereSafe(MEMBERS, "phoneKey", application.phoneKey)).map(asMember);
  if (existing.some((member) => member.status === "active")) {
    throw new Error("Bu telefon ile aktif üye zaten var.");
  }

  const createdAt = nowIso();
  const memberId = makeId("kmem");
  const member = omitUndefined({
    id: memberId,
    fullName: application.fullName,
    nationalId: application.nationalId,
    birthDate: application.birthDate,
    birthPlace: application.birthPlace,
    phone: application.phone,
    phoneKey: application.phoneKey,
    status: "active",
    applicationId: application.id,
    userId: application.userId,
    email: application.email,
    createdAt,
    updatedAt: createdAt,
  });

  await setDoc(doc(db, MEMBERS, memberId), member);
  await updateDoc(doc(db, found.collection, applicationId), {
    status: "approved",
    reviewedAt: createdAt,
    updatedAt: createdAt,
  });
  return asMember(member);
}

export async function rejectKadekonApplicationInFirestore(applicationId, note) {
  const db = requireDb();
  await ensureFirebaseAuth();
  const found = await getApplicationRecord(applicationId);
  if (!found) throw new Error("Başvuru bulunamadı.");
  const application = asApplication({ ...found.row, collection: found.collection });
  if (application.status !== "pending") {
    throw new Error("Bu başvuru zaten işlenmiş.");
  }
  const updatedAt = nowIso();
  await updateDoc(doc(db, found.collection, applicationId), omitUndefined({
    status: "rejected",
    reviewedAt: updatedAt,
    updatedAt,
    reviewNote: String(note || "").trim(),
  }));
}

export async function setKadekonMemberStatusInFirestore(memberId, status) {
  const db = requireDb();
  await ensureFirebaseAuth();
  if (status !== "active" && status !== "inactive") {
    throw new Error("Geçersiz üye durumu.");
  }
  await updateDoc(doc(db, MEMBERS, memberId), {
    status,
    updatedAt: nowIso(),
  });
}

export async function revokeKadekonQrInFirestore(qrId) {
  const db = requireDb();
  await ensureFirebaseAuth();
  const updatedAt = nowIso();
  await updateDoc(doc(db, QRS, qrId), {
    status: "revoked",
    revokedAt: updatedAt,
    updatedAt,
  });
}
